import type { FunctionComponent } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useParams } from "wouter-preact";
import HeaderNav from "../../components/header_nav/header_nav.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { requestGet } from "../../utils/request.ts";
import { useToast } from "../../components/toast/toast.tsx";
import { usePageRefresh, useRequestScope } from "../../hooks/use_page_refresh.ts";

type PreviewPhoto = {
    photo_name: string;
    photo_time: number;
    photo_small_url: string;
    photo_big_url: string;
    photo_download_url: string;
};

type PreviewPhotosResponse = {
    code: number;
    data: {
        camera_name: string;
        photo_count: number;
        photos: PreviewPhoto[];
    };
};

const PAGE_SIZE = 24;

export const PreviewPhotosPage: FunctionComponent = (_) => {
    const t = useTranslator();
    const toast = useToast();
    const params = useParams();
    const link = params["link"] ?? "";
    const cameraId = Number.parseInt(params["cameraId"] ?? "-1");
    const [isBusy, setIsBusy] = useState(false);
    const [cameraName, setCameraName] = useState("");
    const [photoCount, setPhotoCount] = useState(0);
    const [photos, setPhotos] = useState([] as PreviewPhoto[]);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const scope = useRequestScope(`preview-photos:${link}:${cameraId}`);

    useEffect(() => {
        document.title = t({ zh: "预览照片", en: "Preview Photos" });
    }, [t]);

    const loadFirstPage = useMemo(() => async () => {
        if (!link || cameraId <= 0) return;
        const request = scope.begin();
        try {
            setIsBusy(true);
            const resp = await requestGet<PreviewPhotosResponse>("/api/preview_photos", {
                link,
                camera_id: cameraId,
                offset: 0,
                limit: PAGE_SIZE,
            }, request.signal);
            request.check();
            setCameraName(resp.data?.camera_name ?? "");
            setPhotoCount(resp.data?.photo_count ?? 0);
            const lst = resp.data?.photos ?? [];
            setPhotos(lst);
            setOffset(lst.length);
            setHasMore(lst.length >= PAGE_SIZE);
        } catch (e) {
            if (request.current()) toast((e as Error)?.message ?? "Request Failed", "error");
            throw e;
        } finally {
            request.finish();
            if (request.current()) setIsBusy(false);
        }
    }, [link, cameraId, scope]);

    const loadMore = useMemo(() => async () => {
        if (!hasMore || isBusy) return;
        if (!link || cameraId <= 0) return;
        const request = scope.begin();
        try {
            setIsBusy(true);
            const resp = await requestGet<PreviewPhotosResponse>("/api/preview_photos", {
                link,
                camera_id: cameraId,
                offset: offset,
                limit: PAGE_SIZE,
            }, request.signal);
            request.check();
            const lst = resp.data?.photos ?? [];
            setPhotos((prev) => [...prev, ...lst]);
            setOffset((prev) => prev + lst.length);
            if (lst.length < PAGE_SIZE) setHasMore(false);
        } catch (e) {
            if (request.current()) toast((e as Error)?.message ?? "Request Failed", "error");
        } finally {
            request.finish();
            if (request.current()) setIsBusy(false);
        }
    }, [hasMore, isBusy, link, cameraId, offset, scope]);

    const refresh = usePageRefresh({
        pageKey: `preview-photos:${link}:${cameraId}`, enabled: !!link && cameraId > 0,
        refresh: loadFirstPage, canAutoRefresh: offset <= PAGE_SIZE && !isBusy,
    });
    useEffect(() => {
        setPhotos([]);
        setOffset(0);
        setCameraName("");
        setPhotoCount(0);
    }, [link, cameraId]);

    return (
        <div>
            <HeaderNav title={cameraName ? `${t({ zh: "预览", en: "Preview" })}: ${cameraName}` : t({ zh: "预览照片", en: "Preview Photos" })} />
            <div class="container farpov_top_container">
                {refresh.error && <p role="alert">{refresh.error}</p>}
                {refresh.pendingRefresh && <small>{t({ zh: "页面已恢复，可更新照片", en: "Page resumed; photos can be refreshed" })}</small>}
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 1rem;">
                    <small>{t({ zh: "照片数量", en: "Pic" })}: {photoCount}</small>
                    <button type="button" aria-busy={isBusy} disabled={isBusy} onClick={refresh.refreshNow}>
                        {t({ zh: "刷新", en: "Refresh" })}
                    </button>
                </div>
                <hr />
                <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1rem;">
                    {photos.map((p, idx) => (
                        <article key={idx}>
                            <div style="aspect-ratio: 4 / 3; overflow: hidden; border-radius: var(--pico-border-radius);">
                                <img src={p.photo_small_url} style="width: 100%; height: 100%; object-fit: contain;" />
                            </div>
                            <div style="margin-top: 0.5rem;">
                                <small>{p.photo_time > 0 ? new Date(p.photo_time * 1000).toLocaleString() : "-"}</small>
                                <br />
                                <small>{p.photo_name}</small>
                            </div>
                            <footer>
                                <div class="grid">
                                    <button type="button" class="secondary" onClick={() => globalThis.open(p.photo_big_url, "_blank")}>
                                        {t({ zh: "预览", en: "View" })}
                                    </button>
                                    <button type="button" class="secondary" onClick={() => globalThis.open(p.photo_download_url, "_blank")}>
                                        {t({ zh: "下载", en: "Download" })}
                                    </button>
                                </div>
                            </footer>
                        </article>
                    ))}
                </div>
                <div style="margin-top: var(--pico-spacing);">
                    {hasMore ? (
                        <button type="button" aria-busy={isBusy} disabled={isBusy} onClick={loadMore}>
                            {t({ zh: "加载更多", en: "Load More" })}
                        </button>
                    ) : (
                        <small>{t({ zh: "没有更多了", en: "No more" })}</small>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PreviewPhotosPage;
